// @components/form-elements/SmartSelect.jsx
import React, { useEffect, useRef, useState } from "react";
import { FixedSizeList as List } from "react-window";
import { useQuery } from "@tanstack/react-query";
import { fetchJson } from "@utils/fetchJson";
import { API_BASE_URL } from "../../config";
import Cookies from "js-cookie";
import { Check } from "lucide-react";

import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  useDismiss,
  useInteractions,
  useRole,
  useListNavigation,
} from "@floating-ui/react";

const SmartSelect = ({
  id,
  label,
  value,
  onSelect,
  config = {},
  multi: multiProp,
}) => {
  const {
    type = "category",
    source = "",
    check = "",
    list = "",
    placeholder = "Select...",
    allowAddNew = false,
    showRecent = false,
    preload = true,
    cache = true,
    multi: multiConfig = false,
    getValue,
    getLabel,
    customURL = "",
    customParameters = {},
  } = config;

  const multi = multiProp ?? multiConfig;

  // -------------------------
  // STATE
  // -------------------------
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const debounceRef = useRef(null);
  const listRef = useRef(null);
  const listItemsRef = useRef([]);

  // -------------------------
  // DEBOUNCE SEARCH
  // -------------------------
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchTerm(inputValue.trim());
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [inputValue]);

  // -------------------------
  // NORMALIZER
  // -------------------------
  const normalizeOptions = (items = []) =>
    items.map((item) => ({
      value: getValue
        ? getValue(item)
        : item.category_id ?? item.pkKey ?? item.id ?? item.value,
      label: getLabel
        ? getLabel(item)
        : item.categoryName || item.name || item.title || item.label || "",
      original: item,
    }));

  const headers = {
    token: Cookies.get("_bb_key"),
    SadminID: Cookies.get("authid"),
  };

  const keyBase = `${type}-${source}`;
  const queryKey = ["smart-select", keyBase, searchTerm];

  // -------------------------
  // FETCH OPTIONS
  // -------------------------
  const fetchOptions = async () => {
    let res = {};
    let data = [];

    if (type === "category") {
      const url = customURL || `${API_BASE_URL}/categorySlugList`;
      const postData = customURL
        ? customParameters
        : { status: "active", getAll: "Y", slug: source };

      res = await fetchJson(url, {
        method: "POST",
        headers,
        body: JSON.stringify(postData),
      });

      data = customURL ? res.data || [] : res?.data?.[0]?.sublist || [];
    } else {
      res = await fetchJson(`${API_BASE_URL}/searchList`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          text: searchTerm || "",
          system: "new",
          tableName: type === "customer" ? "customer" : source,
          wherec: type === "customer" ? "name" : check,
          status: "false",
          list,
        }),
      });

      data = res?.data || [];
    }

    const normalized = normalizeOptions(data);

    if (showRecent && !searchTerm && cache) {
      localStorage.setItem(
        `recent_${keyBase}`,
        JSON.stringify(normalized.slice(0, 5))
      );
    }

    return normalized;
  };

  // -------------------------
  // REACT QUERY
  // -------------------------
  const {
    data: options = [],
    isFetching,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: fetchOptions,
    enabled: preload || Boolean(searchTerm),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    listItemsRef.current = options.map(
      (_, i) => listItemsRef.current[i] || null
    );
  }, [options]);

  // -------------------------
  // FLOATING UI (NO PORTAL)
  // -------------------------
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift()],
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
  });

  const role = useRole(context, { role: "listbox" });

  const listNav = useListNavigation(context, {
    listRef: listItemsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } =
    useInteractions([dismiss, role, listNav]);

  // -------------------------
  // INPUT EVENTS
  // -------------------------
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (!isOpen) setIsOpen(true);
    setActiveIndex(0);
  };

  const handleInputKeyDown = (e) => {
    // console.log("key", e.key);

    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && activeIndex !== null && options[activeIndex]) {
        handleSelect(options[activeIndex], activeIndex, true);
      }
      return;
    }

    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (e.key === "Tab") {
      setIsOpen(false);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(0);
        setTimeout(() => {
          const el = listItemsRef.current[0];
          if (el) el.focus();
        }, 10);
        return;
      }

      const next =
        activeIndex === null
          ? 0
          : Math.min(activeIndex + 1, options.length - 1);
      setActiveIndex(next);
      setTimeout(() => {
        const el = listItemsRef.current[next];
        if (el) el.focus();
      }, 10);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        const lastIndex = options.length - 1;
        setActiveIndex(lastIndex);
        setTimeout(() => {
          const last = listItemsRef.current[lastIndex];
          if (last) last.focus();
        }, 10);
        return;
      }

      const prev =
        activeIndex === null ? 0 : Math.max(activeIndex - 1, 0);
      setActiveIndex(prev);
      setTimeout(() => {
        const el = listItemsRef.current[prev];
        if (el) el.focus();
      }, 10);
      return;
    }
  };

  const referenceProps = getReferenceProps({
    onKeyDown: handleInputKeyDown,
    onChange: handleInputChange,
    onClick: () => {
      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(0);
      }
    },
  });

  // Auto-scroll highlighted item
  useEffect(() => {
    if (listRef.current && activeIndex !== null && isOpen) {
      listRef.current.scrollToItem(activeIndex, "smart");
    }
  }, [activeIndex, isOpen]);

  // -------------------------
  // SELECT LOGIC
  // -------------------------
  const normalizedValueArray = Array.isArray(value)
    ? value
    : value
    ? [value]
    : [];

  const handleSelect = (item, index, triggeredByKeyboard = false) => {
    const isAlreadySelected = normalizedValueArray.some(
      (v) => v.value === item.value
    );

    if (multi) {
  const found = normalizedValueArray.some(v => v.value === item.value);
  const updated = found
    ? normalizedValueArray.filter(v => v.value !== item.value)
    : [...normalizedValueArray, { value: item.value, label: item.label }];

  onSelect(updated);

  // RESET INPUT + SEARCH
  setInputValue("");
  setSearchTerm("");
  setActiveIndex(null);
  setSelectedIndex(index);

  // keep dropdown open + return focus to search
  setTimeout(() => refs.domReference.current?.focus(), 0);
  return;
} else {
      onSelect(item);
      setSelectedIndex(index);
      setIsOpen(false);
      setActiveIndex(null);

      // Optionally clear search text after closing
      setTimeout(() => setInputValue(""), 0);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue("");

    if (multi) onSelect([]);
    else onSelect(null);

    setIsOpen(false);
    setActiveIndex(null);
    setSelectedIndex(null);
  };

  const handleRefresh = async () => {
    localStorage.removeItem(`recent_${keyBase}`);
    await refetch();
  };

  // -------------------------
  // VIRTUAL LIST ROW
  // -------------------------
  const Row = ({ index, style }) => {
    const item = options[index];
    const isActive = activeIndex === index;
    const isSelected = normalizedValueArray.some(
      (v) => v?.value === item.value
    );

    return (
      <div
        ref={(node) => {
          listItemsRef.current[index] = node;
        }}
        role="option"
        aria-selected={isActive}
        tabIndex={isActive ? 0 : -1}
        style={{
          ...style,
          backgroundColor: isActive ? "#E8F0FE" : "transparent",
        }}
        {...getItemProps({
          onClick: () => handleSelect(item, index, false),
          onKeyDown: (e) => handleInputKeyDown(e),
        })}
        className="px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors outline-none"
      >
        <span className="truncate">{item.label}</span>
        {isSelected && <Check size={16} className="text-green-600" />}
      </div>
    );
  };

  // -------------------------
  // DISPLAY VALUE
  // -------------------------
const getDisplayValue = () => {
  // MULTI: always let user type freely
  if (multi) return inputValue;

  // SINGLE SELECT:
  if (inputValue) return inputValue;
  return normalizedValueArray[0]?.label || "";
};

  // -------------------------
  // RENDER
  // -------------------------
  return (
    <div className="relative w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={refs.setReference}
          type="text"
          value={getDisplayValue()}
          placeholder={placeholder}
          className="form-input w-full border px-3 py-2 pr-10 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoComplete="off"
          {...referenceProps}
        />

        {(inputValue || normalizedValueArray.length > 0) && (
          <button
            type="button"
            onClick={handleClear}
            tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-red-600 font-bold"
          >
            ✕
          </button>
        )}
      </div>

      {/* CHIPS */}
      {multi && normalizedValueArray.length > 0 && !inputValue && (
        <div className="flex flex-wrap gap-1 mt-2">
          {normalizedValueArray.map((item, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
            >
              {item.label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const index = options.findIndex(
                    (opt) => opt.value === item.value
                  );
                  handleSelect(item, index, false);
                }}
                className="hover:text-red-600 ml-1"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {/* FLOATING DROPDOWN (NO PORTAL) */}
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            minWidth: refs.domReference.current?.offsetWidth || 240,
            maxWidth: 480,
            zIndex: 9999,
            position: floatingStyles.position,
          }}
          className="absolute border bg-white shadow-lg rounded-md text-sm"
          {...getFloatingProps()}
        >
          <div className="px-3 py-2 bg-blue-50 border-b flex justify-between items-center">
            <button
              type="button"
              onClick={handleRefresh}
              className="text-xs text-blue-600 hover:underline"
            >
              Refresh list
            </button>
            {isFetching && (
              <span className="text-[10px] text-gray-500">loading…</span>
            )}
          </div>

          {allowAddNew && inputValue && (
            <div
              className="px-4 py-2 text-indigo-600 cursor-pointer hover:bg-indigo-50 border-b"
              onClick={() =>
                handleSelect(
                  { value: `new_${Date.now()}`, label: inputValue },
                  -1,
                  false
                )
              }
            >
              + Add New: {inputValue}
            </div>
          )}

          {isFetching && options.length === 0 ? (
            <div className="p-3 text-gray-500 text-center">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-4 py-3 text-gray-400 text-center">
              No results found
            </div>
          ) : (
            <List
              ref={listRef}
              height={Math.min(options.length * 44, 220)}
              itemCount={options.length}
              itemSize={44}
              width="100%"
            >
              {Row}
            </List>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartSelect;
